import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'

const MANUAL_SECTIONS = [
  { id: 'rules', title: "Core Trading Rules", icon: '◈', content: [
    { h: 'Rule 1 — Only trade stocks you\'d be proud to own', body: 'Every CSP you sell is a promise: "I will buy 100 shares of this stock at this price." If you would be embarrassed or scared to own it, don\'t sell the put. This single filter eliminates most bad trades before they happen.' },
    { h: 'Rule 2 — Never sell options into earnings', body: 'In the two weeks before an earnings announcement, IV inflates dramatically. After the announcement, IV collapses — often 30–50% overnight. If you sold a CSP before earnings, you collected inflated premium but held through a potential 20%+ gap. The risk/reward is terrible. Always check earnings dates before entering any options position.' },
    { h: 'Rule 3 — Know your cost basis before selling covered calls', body: 'Your covered call strike must always be at or above your cost basis on the shares. If you paid $62 for KO and sell the $60 call, you\'re locking in a loss if assigned. Know your number. Sell above it.' },
    { h: 'Rule 4 — The 2x rule on 0DTE', body: 'On same-day SPX credit spreads, if the position reaches 2× the credit received, close it immediately. No exceptions. No "it will come back." You received $1.20 — at $2.40 you close, take the $1.20 loss, and protect your capital for the next trade.' },
    { h: 'Rule 5 — Paper trade for 30 days minimum', body: 'Paper trading is not optional. It teaches you the platform, surfaces your psychological tendencies, and lets you learn the mechanics of assignment without real consequences. Anyone who skips this step pays for it in real money later.' },
    { h: 'Rule 6 — Position size is risk management', body: 'Never allocate more than 10–15% of your account to a single underlying. Never risk more than 1–2% of your account on any single trade. Never have more than 5% of your total portfolio at risk across all open positions simultaneously. Proper sizing means a bad trade is a lesson, not a disaster.' },
  ]},
  { id: 'csp', title: 'Cash-Secured Put Playbook', icon: '◉', content: [
    { h: 'Setup Checklist', body: 'Stock above 200-day MA • IV Rank 30–70 • No earnings within 30 days • Open interest above 500 on the target strike • Strike below key support level • You\'d be happy owning at this price' },
    { h: 'Strike Selection', body: 'Beginners: target the 0.15 delta strike for all selling strategies — approximately 85% probability of expiring worthless. Further OTM = more safety, less premium. Closer ATM = more premium, more risk. The progression: 0.15 (start here) → 0.20-0.25 (intermediate) → 0.30 (experienced). Master each level before advancing.' },
    { h: 'DTE Selection', body: 'Professional sellers target 30–45 DTE. This captures the best theta decay curve — options lose the most time value in the final 30 days. Avoid going under 21 DTE on new entries (gamma risk increases). Close at 50% profit, typically 10–20 days before expiration.' },
    { h: 'Exit Rules', body: 'Close at 50% of maximum profit (buy back for half the premium received). This locks in gains and frees capital for the next trade. If the position goes against you to 2× the credit, close and reassess. Never let a defined-income trade become an undefined loss.' },
  ]},
  { id: 'wheel', title: 'Wheel Strategy Reference', icon: '◬', content: [
    { h: 'The Three Phases', body: 'Phase 1 — Sell CSP: Collect premium. If the put expires worthless, collect premium and repeat. If assigned, move to Phase 2. Phase 2 — Sell Covered Calls: You now own 100 shares. Sell calls at or above your cost basis each expiration. If the call expires worthless, repeat. If assigned (shares called away), move to Phase 3. Phase 3 — Restart: You\'ve collected the stock price + all the premium. Start the Wheel again on the same stock.' },
    { h: 'Best Wheel Stocks', body: 'Price $20–$150 (affordable for 100 shares) • Stable business with strong brand (KO, T, F, SBUX, INTC) • IV Rank above 30 for good premium • Dividend-paying preferred (extra income while wheeling) • No upcoming catalysts that could cause 20%+ moves • Liquid options chain (spreads under $0.05)' },
    { h: 'Cost Basis Math', body: 'Example: Buy KO at $62 via assignment. Sell $62 covered call for $0.70. New cost basis: $61.30. Next month, sell for $0.65. New cost basis: $60.65. After 6 months of wheeling, cost basis might be $57–$58 on a stock you bought at $62. Every premium collected is permanent risk reduction.' },
  ]},
  { id: 'psychology', title: 'Trading Psychology', icon: '◧', content: [
    { h: 'The Three Enemies', body: 'Fear: Makes you close profitable positions too early and avoid good setups. Greed: Makes you hold losers too long and size up on winners. FOMO: Makes you chase missed moves and enter suboptimal trades. Every bad trade can be traced to one of these three.' },
    { h: 'The Journal Rule', body: 'After every closed trade, write: What was my thesis? Was I right? What would I do differently? Traders who journal improve faster than those who don\'t — not because the writing is magic, but because it forces honest self-assessment that\'s easy to avoid otherwise.' },
    { h: 'Daily Loss Limits', body: 'Set a maximum loss per day before you open your platform. A common professional guideline is 2% of account. If you hit it, close everything and step away. No revenge trading. No "one more trade to get it back." The market will be there tomorrow. Your capital needs to be too.' },
    { h: 'Detachment from Outcomes', body: 'A losing trade that followed your rules is a good trade. A winning trade that violated your rules is a dangerous one. Judge yourself on process, not outcomes. Over hundreds of trades, a sound process wins. Over any single trade, anything can happen.' },
  ]},
];

// ═══════════════════════════════════════════════════════════════
// ROLL CALCULATOR VIEW
// ═══════════════════════════════════════════════════════════════
function RollCalculatorView({ onNavigate }) {
  const [form, setForm] = useState({
    symbol: '', stockPrice: '', strikePrice: '', originalPremium: '',
    currentOptionPrice: '', daysToExpiration: '', rollCredit: '',
    newStrike: '', coveredCallPremium: '', contracts: '1', accountSize: '',
  });

  const f = (v) => parseFloat(v) || 0;
  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const sp    = f(form.stockPrice);
  const strk  = f(form.strikePrice);
  const orig  = f(form.originalPremium);
  const curr  = f(form.currentOptionPrice);
  const dte   = f(form.daysToExpiration);
  const rc    = f(form.rollCredit);
  const ns    = f(form.newStrike);
  const cc    = f(form.coveredCallPremium);
  const qty   = Math.max(1, Math.round(f(form.contracts)));
  const acct  = f(form.accountSize);

  const inTheMoney  = sp > 0 && strk > 0 && sp < strk;
  const intrinsicV  = inTheMoney ? strk - sp : 0;
  const totalPremium = orig * qty * 100;
  const closeDebit  = curr * qty * 100;
  const closePnL    = totalPremium - closeDebit;
  const closePct    = acct > 0 ? (closePnL / acct * 100) : null;

  const rollNetCredit = rc * qty * 100;
  const rollNewCostBasis = ns > 0 ? ns - (orig + rc) : null;
  const rollPnLNote = rc > 0 ? `+$${(rc * qty * 100).toFixed(2)} additional credit collected` : null;

  const assignCostBasis = strk - orig;
  const assignPaperGain = sp > 0 ? (sp - assignCostBasis) * qty * 100 : null;
  const ccIncome = cc * qty * 100;
  const assignNextCostBasis = cc > 0 ? assignCostBasis - cc : null;

  const twoXDebit = closeDebit * 2;
  const twoXBreached = closeDebit > totalPremium * 2;

  const hasInputs = sp > 0 && strk > 0 && orig > 0 && curr > 0;

  const inputStyle = {
    background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px',
    color: V.text, padding: '9px 12px', fontSize: '13px', fontFamily: "'Jost',sans-serif",
    width: '100%', boxSizing: 'border-box', outline: 'none',
  };
  const labelStyle = { fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '4px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.08em' };
  const resultCard = (title, color, children) => (
    <div style={{ background: V.bg2, border: `0.5px solid ${color}44`, borderTop: `3px solid ${color}`, borderRadius: '10px', padding: '18px 20px' }}>
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 600, color, marginBottom: '12px' }}>{title}</div>
      {children}
    </div>
  );
  const row = (label, value, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `0.5px solid ${V.border}22` }}>
      <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted }}>{label}</span>
      <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 600, color: color || V.text }}>{value}</span>
    </div>
  );
  const fmt = (n) => n >= 0 ? `+$${n.toFixed(2)}` : `-$${Math.abs(n).toFixed(2)}`;
  const fmtPct = (n) => n >= 0 ? `+${n.toFixed(2)}%` : `${n.toFixed(2)}%`;

  return (
    <div style={{ padding: '28px 32px', maxWidth: '860px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
      <button style={{ ...btnGhost, marginBottom: '20px', fontSize: '12px' }} onClick={() => onNavigate && onNavigate('dashboard')}>← Dashboard</button>
      <div style={{ ...eyebrow, marginBottom: '8px' }}>The Wheel — Trade Management</div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, margin: '0 0 4px' }}>Roll vs Close vs Assign</h2>
      <p style={{ fontSize: '13px', color: V.muted, marginBottom: '24px' }}>Model all three scenarios with real numbers before you decide.</p>

      {/* Input grid */}
      <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '22px', marginBottom: '24px' }}>
        <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', fontWeight: 600, color: V.gold, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Position Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          {[
            ['symbol', 'Symbol', 'e.g. KO'],
            ['stockPrice', 'Current Stock Price', 'e.g. 58.00'],
            ['strikePrice', 'Short Put Strike', 'e.g. 62.00'],
            ['originalPremium', 'Original Premium (per share)', 'e.g. 0.85'],
            ['currentOptionPrice', 'Current Option Price', 'e.g. 1.40'],
            ['daysToExpiration', 'Days to Expiration', 'e.g. 14'],
            ['contracts', 'Contracts', '1'],
            ['accountSize', 'Options Buying Power (optional)', 'e.g. 25000'],
          ].map(([key, label, ph]) => (
            <div key={key}>
              <label style={labelStyle}>{label}</label>
              <input style={inputStyle} placeholder={ph} value={form[key]} onChange={e => upd(key, e.target.value)} />
            </div>
          ))}
        </div>
        <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', fontWeight: 600, color: V.gold, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '4px 0 12px' }}>Roll & Assignment Inputs</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
          {[
            ['rollCredit', 'Roll Net Credit (per share)', 'e.g. 0.35'],
            ['newStrike', 'New Strike (after roll)', 'e.g. 58.00'],
            ['coveredCallPremium', 'Covered Call Premium (if assigned)', 'e.g. 0.70'],
          ].map(([key, label, ph]) => (
            <div key={key}>
              <label style={labelStyle}>{label}</label>
              <input style={inputStyle} placeholder={ph} value={form[key]} onChange={e => upd(key, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      {/* Current position status */}
      {hasInputs && (
        <div style={{ background: inTheMoney ? 'rgba(196,122,122,0.06)' : 'rgba(122,171,138,0.06)', border: `0.5px solid ${inTheMoney ? V.rose : V.sage}44`, borderRadius: '10px', padding: '14px 18px', marginBottom: '24px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div>
            <div style={labelStyle}>Position Status</div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', fontWeight: 600, color: inTheMoney ? V.rose : V.sage }}>
              {inTheMoney ? `In-the-money — $${intrinsicV.toFixed(2)} intrinsic value` : 'Out-of-the-money — still protected'}
            </div>
          </div>
          <div>
            <div style={labelStyle}>Total Premium Collected</div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', fontWeight: 600, color: V.sage }}>${totalPremium.toFixed(2)}</div>
          </div>
          <div>
            <div style={labelStyle}>Cost to Close Now</div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', fontWeight: 600, color: closeDebit > totalPremium ? V.rose : V.text }}>${closeDebit.toFixed(2)}</div>
          </div>
          {dte > 0 && (
            <div>
              <div style={labelStyle}>Days to Expiration</div>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', fontWeight: 600, color: V.text }}>{dte}</div>
            </div>
          )}
        </div>
      )}

      {/* 2x Rule alert */}
      {hasInputs && twoXBreached && (
        <div style={{ background: 'rgba(196,122,122,0.08)', border: `1px solid ${V.rose}55`, borderRadius: '10px', padding: '14px 18px', marginBottom: '24px' }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 600, color: V.rose, marginBottom: '4px' }}>⚠ 2× Rule Triggered</div>
          <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, margin: 0, lineHeight: 1.65 }}>
            The cost to close (${closeDebit.toFixed(2)}) exceeds 2× the original premium collected (${(totalPremium * 2).toFixed(2)}). Many traders use the 2× rule as a hard close trigger. Consider closing now rather than rolling.
          </p>
        </div>
      )}

      {/* Three scenario cards */}
      {hasInputs && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {resultCard('Option A — Close the Position', V.rose,
            <>
              {row('Debit to close', `$${closeDebit.toFixed(2)}`, V.rose)}
              {row('Net P&L on trade', fmt(closePnL), closePnL >= 0 ? V.sage : V.rose)}
              {closePct !== null && row('P&L as % of account', fmtPct(closePct), closePct >= 0 ? V.sage : V.rose)}
              {row('Capital freed', `$${(strk * qty * 100).toFixed(2)}`, V.muted)}
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, fontStyle: 'italic', marginTop: '10px', lineHeight: 1.6 }}>
                Best when: thesis has changed, stock is broken, or 2× rule triggered.
              </p>
            </>
          )}
          {resultCard('Option B — Roll Down & Out', V.gold,
            <>
              {rc > 0 ? (
                <>
                  {row('Net credit from roll', `+$${rollNetCredit.toFixed(2)}`, V.sage)}
                  {ns > 0 && row('New effective cost basis', `$${rollNewCostBasis.toFixed(2)}/share`, V.text)}
                  {row('New strike', ns > 0 ? `$${ns.toFixed(2)}` : '—', V.text)}
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, fontStyle: 'italic', marginTop: '10px', lineHeight: 1.6 }}>
                    Best when: stock pulled back temporarily, you still want to own it, and you can collect meaningful credit.
                  </p>
                </>
              ) : (
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim, fontStyle: 'italic', margin: 0 }}>
                  Enter Roll Net Credit and New Strike above to model this scenario.
                </p>
              )}
            </>
          )}
          {resultCard('Option C — Take Assignment', V.sage,
            <>
              {row('Cost basis per share', `$${assignCostBasis.toFixed(2)}`, V.text)}
              {assignPaperGain !== null && row('Paper gain vs current price', fmt(assignPaperGain), assignPaperGain >= 0 ? V.sage : V.rose)}
              {cc > 0 ? (
                <>
                  {row('First covered call income', `+$${ccIncome.toFixed(2)}`, V.sage)}
                  {row('Cost basis after CC', `$${assignNextCostBasis.toFixed(2)}/share`, V.gold)}
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, fontStyle: 'italic', marginTop: '10px', lineHeight: 1.6 }}>
                    Assignment is the Wheel completing its first turn. Sell covered calls immediately and keep reducing your cost basis.
                  </p>
                </>
              ) : (
                <>
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, fontStyle: 'italic', marginTop: '10px', lineHeight: 1.6 }}>
                    Enter Covered Call Premium above to see your next-step income projection.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Summary comparison table */}
      {hasInputs && (
        <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', fontWeight: 600, color: V.gold, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '14px' }}>Summary Comparison</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Jost',sans-serif", fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: `0.5px solid ${V.border}` }}>
                  {['Scenario', 'Immediate P&L', 'Capital Status', 'Ongoing Income'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: V.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '10px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: `0.5px solid ${V.border}22` }}>
                  <td style={{ padding: '10px 12px', color: V.rose, fontWeight: 600 }}>Close</td>
                  <td style={{ padding: '10px 12px', color: closePnL >= 0 ? V.sage : V.rose }}>{fmt(closePnL)}</td>
                  <td style={{ padding: '10px 12px', color: V.sage }}>Capital freed</td>
                  <td style={{ padding: '10px 12px', color: V.muted }}>None — position closed</td>
                </tr>
                {rc > 0 && (
                  <tr style={{ borderBottom: `0.5px solid ${V.border}22` }}>
                    <td style={{ padding: '10px 12px', color: V.gold, fontWeight: 600 }}>Roll</td>
                    <td style={{ padding: '10px 12px', color: V.sage }}>+${rollNetCredit.toFixed(2)} credit</td>
                    <td style={{ padding: '10px 12px', color: V.muted }}>Capital still at risk</td>
                    <td style={{ padding: '10px 12px', color: V.sage }}>Collecting premium to new expiry</td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: '10px 12px', color: V.sage, fontWeight: 600 }}>Assign</td>
                  <td style={{ padding: '10px 12px', color: V.muted }}>Unrealized</td>
                  <td style={{ padding: '10px 12px', color: V.text }}>Own 100 shares @ ${assignCostBasis.toFixed(2)}</td>
                  <td style={{ padding: '10px 12px', color: cc > 0 ? V.sage : V.muted }}>{cc > 0 ? `+$${ccIncome.toFixed(2)}/mo (CC)` : 'Sell covered calls'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!hasInputs && (
        <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
          <p style={{ ...serif, fontSize: '16px', color: V.muted, fontStyle: 'italic', margin: 0 }}>
            Enter your position details above to model all three scenarios.
          </p>
        </div>
      )}

      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', padding: '14px 18px', marginTop: '8px' }}>
        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, fontStyle: 'italic', margin: 0, lineHeight: 1.7 }}>
          Remember: the goal of The Wheel is to stay in the trade as long as the thesis holds. Rolling and assignment are not failures — they are the strategy working as designed. Closing is only wrong when you do it in panic. It is right when the thesis is gone.
        </p>
      </div>
    </div>
  );
}

export { MANUAL_SECTIONS }
export default RollCalculatorView
